// Created: 2026-08-14
// Updated: 2026-08-14
//
// The single source of key truth for the NOTIFICATION section (react-query-notifications, Req 1).
//
// Inherited conventions (slices 1–5): keys are hierarchical arrays of JSON-serializable primitives and
// plain objects, element 0 names the resource, PERSONAL data is namespaced under a leading 'me' and its
// query is always `enabled: !!idToken`. A parameter belongs in a key only when it selects a DIFFERENT
// resource, not when it merely filters one. NO CURSOR APPEARS IN ANY KEY — the list cursor rides in
// `pageParam` (slice 4 LIST rule 1); see `queries.ts`.
//
// ─── THIS SLICE HAS NO PUBLIC KEY, AND THAT IS THE STRUCTURAL FACT ────────────────────────────────
//
// Slices 1–5 each had to argue which of their reads were identity-INVARIANT and therefore safe to share
// (and later to prerender). Notifications have none. Every notification read is on `/api/secure`, which
// is the COGNITO-authorized API — `infra/cdk/src/constructs/api-construct.ts` lines 75 and 92 — in
// contrast to the `/api` proxy at line 49, which carries NO `authorizationType` and is what every public
// key in slices 1–5 reads from. And every handler resolves the caller, refusing without one:
//
//   GET  /notifications/unread-count   lambda-secure/src/routes/notifications.ts:29
//   GET  /notifications                lambda-secure/src/routes/get-notifications-paginated.ts:24
//   PATCH /notifications/{id}/read     lambda-secure/src/routes/notifications.ts:66
//   DELETE /notifications/{id}         lambda-secure/src/routes/notifications.ts:119
//
// — each `getIdentityId(event)` then `return errorResponse(401, 'Unauthorized')` when it is absent. And
// every DynamoDB access underneath is keyed on that identity: `PK = user#{userId}` at
// `lambda-secure/src/services/notifications.ts:710` (list all), `:734` (list by category), `:753`
// (unread counts), `:628` (mark read), `:673` (delete).
//
// So EVERY key here is `'me'`-namespaced, every query is `enabled: !!idToken`, and NOTHING in this
// section is ever dehydrated into shared HTML or prerendered by the later SSG/ISR step. This section is
// the personal half of the public/personal split — the data that must never reach a CDN-cached page.

import type { NotificationCategory } from '@mrb91002/shared-types'

/**
 * Stale window for the unread count AND for every category list — applied as per-query overrides, never
 * as a client default.
 *
 * WHY OVERRIDE THE 5-MINUTE BASELINE. Notifications are the one surface in this migration whose data is
 * moved almost entirely by OTHER PEOPLE: somebody follows you, comments on your pattern, reviews your
 * material, a moderator decides something. This client performs none of those writes, so it can never
 * invalidate them, and the stale window is the ONLY lever — the identical argument slice 5 recorded for
 * `follower_count`. A badge five minutes behind on a page the user just navigated to is the failure
 * mode. 30 s keeps the within-visit navigation win (leaving `/notifications` and returning inside half a
 * minute costs no request) while bounding staleness to under the human "did that arrive?" loop.
 *
 * WHY NOT SHORTER. Pre-migration the bell fetched its count ONCE per session, so 30 s is already a large
 * improvement; and the bell mounts on EVERY authenticated page, so a shorter window multiplies requests
 * on every route change for a number that is decorative between events.
 *
 * WHY ONE CONSTANT SERVES BOTH THE COUNT AND THE LISTS — deliberately unlike slice 5, which declared
 * `PROFILE_STALE_TIME_MS` and `COLLECTION_STALE_TIME_MS` separately because their equality was
 * COINCIDENTAL. Here the coupling is CAUSAL: one external event (a notification is created) moves the
 * list row AND the counter record, and the two are read by the SAME two surfaces on the same screen.
 * Tuning one without the other would produce a screen whose badge and list disagree — which is the exact
 * defect this slice exists to remove. So: one constant, and this paragraph so nobody "fixes" it into two.
 *
 * POLLING IS OUT OF SCOPE, explicitly. No `refetchInterval`, no `refetchOnWindowFocus` override. A poll
 * is a new recurring wire cost for every authenticated session on every page — a load and cost decision,
 * not a migration decision. Push delivery is what the later real-time step attaches to this same cache,
 * and a poll added now is the first thing that step would remove (Req 7.5).
 */
export const NOTIFICATION_STALE_TIME_MS = 30_000

/**
 * Collapse `undefined` and `'all'` to `'all'` BEFORE the value reaches either the key or the fetcher.
 *
 * This is slice 4's rule 2 ("normalize before keying — and pass the normalized value to the fetcher
 * too") doing real work rather than ceremony. `fetchNotificationsPaginated` sets the `category`
 * parameter only WHEN `category && category !== 'all'`, so the nav bell's
 * `fetchNotificationsPaginated(null, token)` and the Notification Center's
 * `fetchNotificationsPaginated(null, token, 'all')` produce the BYTE-IDENTICAL URL
 * `{SECURE_API_BASE}/notifications`. Without normalization those two callers would mint TWO cache
 * entries and issue TWO requests for ONE resource — the cache-splitting hazard slice 4 catalogued, and
 * the second consolidation this slice performs (design § D3).
 *
 * Idempotent by construction: `'all'` maps to `'all'`, so `normalizeCategory(normalizeCategory(x))`
 * equals `normalizeCategory(x)` for every input.
 */
export function normalizeCategory(category?: NotificationCategory): NotificationCategory {
  return category && category !== 'all' ? category : 'all'
}

export const notificationKeys = {
  /**
   * PERSONAL — the caller's unread counts, all five numbers in one entry
   * (`{count, patterns, materials, social, system}`).
   *
   * **THIS IS THE CONSOLIDATED ENTRY, and consolidating it is the point of the slice.** Before slice 6
   * the count had three implementations, two of them live in front of a user:
   *
   *   A. `UserRelationsContext.unreadNotificationCount`, set from the bulk `fetchUserRelations` payload
   *      and fetched ONCE per `idToken` for the whole session (a `lastFetchedIdToken` ref guard), never
   *      refreshed by a mark-read or a delete → read by the nav bell's badge.
   *   B. `NotificationCenter`'s own `getUnreadNotificationCount` effect behind a `countsLoaded` latch,
   *      then hand-decremented on every write → read by the five category badges.
   *   C. `hooks/useNotifications.ts`'s own effect plus manual `refreshUnreadCount` /
   *      `decrementUnreadCount` / `resetUnreadCount` → read by NOBODY (zero importers).
   *
   * A and B are the SAME NUMBER at the moment of fetch — both read the DynamoDB record
   * `user#{identity_id}` / `notification_unread_counter`, attribute `unread_count`:
   * `lambda-secure/src/routes/get-user-relations.ts:107-113` for A,
   * `lambda-secure/src/services/notifications.ts:752-762` for B (which remaps it to `count`). They
   * diverge only through FETCH DISCIPLINE, which is why one cache entry fixes it with no backend change
   * and why moving the bell onto this entry changes the number's freshness, never its definition.
   *
   * Since slice 6 BOTH badge surfaces read this one entry through `hooks/useNotifications.ts`, and the two
   * notification mutations are its only invalidators. Slice 6 left A's context field in place, unread,
   * because removing it meant rewriting the provider (design § D2 enumerated that cleanup); slice 7
   * (react-query-user-relations) did the rewrite and REMOVED it, so `UserRelationsContext` is now a
   * derivation over `['me','relations']` carrying no notification data.
   *
   * THIS KEY IS THEREFORE THE ONLY SOURCE OF AN UNREAD COUNT, and it must stay that way. The reason a count
   * earns its own entry is not tidiness — it is that an entry has its own INVALIDATORS. A count riding
   * inside a bulk payload is refreshed on that payload's schedule, which no notification write participates
   * in, so it drifts the instant anything is read or deleted. Do not re-add one to a bulk response and do
   * not read one from a payload that is not this entry.
   */
  unreadCount: () => ['me', 'notifications', 'unreadCount'] as const,

  /**
   * PERSONAL — one category's notifications, accumulated across pages in ONE entry.
   *
   * `category` IS RESOURCE IDENTITY, NOT A FILTER. `?category=patterns` runs
   * `listNotificationsByCategory` — `begins_with(SK, 'notification#patterns#')`
   * (`services/notifications.ts:733-735`) — while its absence runs `listNotifications` —
   * `begins_with(SK, 'notification#')` (`:709-713`). Different DynamoDB query, different row set: a
   * different resource, so it belongs in the key. Same test slice 4 applied to `limit` and slice 5 to
   * `minimal`.
   *
   * The category is NORMALIZED on the way in, which is what makes the bell and the "All" tab one entry.
   */
  list: (category?: NotificationCategory) =>
    ['me', 'notifications', 'list', { category: normalizeCategory(category) }] as const,
}

//
// ─── WHY THERE IS NO LIST *PREFIX* KEY ────────────────────────────────────────────────────────────
//
// Slice 5 established that a prefix (`myListPrefix()` → `['me','collections']`) is the right tool for
// "one call, every mounted variant". It is the WRONG tool here, and the omission is a decision.
//
// A notification row's SK is `notification#{category}#{paddedMs}#{id}`
// (`services/notifications.ts:124-127`), so the row appears in EXACTLY TWO queries: the `all` query and
// its own category's query. Never in a sibling category. `['me','notifications','list']` would reach all
// five entries and invalidate four categories that no write changed — over-invalidation, which slice 5's
// § 3 forbids exactly as firmly as under-invalidation. The correct reach for every write in this slice
// is the precise PAIR `list('all')` + `list(category)`, so no prefix is exported and there is nothing to
// reach for by accident. See the invalidation matrix in `mutations.ts` (Req 1.8, 5.3).
//

/** The union of every key this factory emits — used by the key tests. */
export type NotificationKey = ReturnType<(typeof notificationKeys)[keyof typeof notificationKeys]>
